import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { ApiClientError } from '@/lib/http-core';
import { ADMIN_MEMO_MAX_LENGTH, useOrderAdminMemoMutations } from '../hooks/useOrderAdminMemos';
import { PaymentSyncNotReadyError, useOrderPaymentSyncMutation } from '../hooks/useOrderPaymentSyncMutation';

const PG_STATUS_LABEL: Record<string, string> = {
  DONE: '결제완료',
  CANCELED: '전체 취소',
  PARTIAL_CANCELED: '부분 취소',
  WAITING_FOR_DEPOSIT: '입금 대기',
  ABORTED: '승인 실패',
  EXPIRED: '만료',
};

/** 동기화 결과 → 운영자 안내. 확인 필요(부분 환불·출고 후 등)면 BE 사유를 붙인다. */
export const paymentSyncResultMessage = (r: {
  changed: boolean;
  pgStatus: string | null;
  reviewRequired?: boolean;
  reviewReason?: string | null;
}): string => {
  const pg = r.pgStatus ? (PG_STATUS_LABEL[r.pgStatus] ?? r.pgStatus) : '확인 불가';
  if (r.reviewRequired) {
    return `PG 상태(${pg})는 결제에만 반영했고 주문은 자동 취소하지 않았습니다 — 확인 필요: ${r.reviewReason ?? '운영자 확인이 필요합니다.'}`;
  }
  return r.changed
    ? `PG 상태(${pg})를 주문에 반영했습니다.`
    : `PG 상태(${pg})와 주문이 이미 같습니다. 바뀐 것이 없습니다.`;
};

/**
 * QA-432 — [PG 상태 동기화]. 토스에 결제를 다시 조회해 주문에 반영하고, 운영자가 쓴 사유를 관리자 메모로 남긴다.
 * 결제상태를 손으로 고르지 않는다(마일리지·재고·쿠폰 복원이 어긋나지 않게 — BE 가 PG 기준으로 처리).
 */
export const OrderPaymentSyncDialog = ({
  orderId,
  open,
  onOpenChange,
}: {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('orders');
  const sync = useOrderPaymentSyncMutation(orderId);
  const { create: createMemo } = useOrderAdminMemoMutations(orderId);
  const [memo, setMemo] = useState('');

  const close = () => {
    onOpenChange(false);
    setMemo('');
    sync.reset();
  };

  const run = () => {
    sync.mutate(undefined, {
      onSuccess: (result) => {
        const message = paymentSyncResultMessage(result);
        // 확인 필요면 경고로 — 주문 상태가 그대로라 운영자가 클레임·수동 처리를 이어가야 한다.
        if (result.reviewRequired) toast.warning(message);
        else toast.success(message);
        const reason = memo.trim();
        // 사유 + 결과를 주문 관리자 메모로 남긴다(누가 언제 왜 맞췄는지). 메모 실패는 동기화 결과를 되돌리지 않는다.
        createMemo.mutate(
          { scope: 'ORDER', category: 'REFUND', content: `[PG 상태 동기화] ${message}${reason ? `\n${reason}` : ''}` },
          { onError: (e) => toast.error(friendlyMessage(e, '관리자 메모를 남기지 못했습니다.')) },
        );
        close();
      },
    });
  };

  const error = sync.error
    ? sync.error instanceof PaymentSyncNotReadyError
      ? t('orders.paymentSync.notReady', { defaultValue: 'PG 상태 동기화는 서버(BE) 반영 후 사용할 수 있습니다.' })
      : sync.error instanceof ApiClientError && sync.error.status === 503
        ? t('orders.paymentSync.unavailable', { defaultValue: '결제 서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.' })
        : friendlyMessage(sync.error, t('orders.paymentSync.fail', { defaultValue: 'PG 상태를 확인하지 못했습니다.' }))
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('orders.paymentSync.title', { defaultValue: 'PG 상태 동기화' })}</DialogTitle>
          <DialogDescription>
            {t('orders.paymentSync.description', {
              defaultValue:
                '토스 결제 상태를 다시 조회해 주문에 반영합니다. 토스 상점관리자에서 취소·환불한 주문이면 주문이 취소완료로 바뀌고 사용한 마일리지·재고가 되돌아갑니다.',
            })}
          </DialogDescription>
        </DialogHeader>
        <label className="flex flex-col gap-1 text-sm">
          {t('orders.paymentSync.memo', { defaultValue: '관리자 메모' })}
          <textarea
            rows={3}
            maxLength={ADMIN_MEMO_MAX_LENGTH - 100}
            className="w-full rounded-md px-2.5 py-1.5 text-[13px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
            style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)', color: 'var(--content-default)' }}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder={t('orders.paymentSync.memoPlaceholder', { defaultValue: '예: 고객 요청으로 토스 상점관리자에서 전액 환불(10/20 14:04)' })}
            data-testid="payment-sync-memo"
          />
          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {t('orders.paymentSync.memoHint', { defaultValue: '조회 결과와 함께 주문 관리자 메모(환불)로 남습니다.' })}
          </span>
        </label>
        {error ? (
          <p className="text-[12px]" style={{ color: 'var(--signal-danger)' }} role="alert" data-testid="payment-sync-error">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost" size="sm">
              {t('orders.actions.cancel', { defaultValue: '취소' })}
            </Button>
          </DialogClose>
          <Button
            type="button"
            size="sm"
            disabled={sync.isPending || memo.trim().length === 0}
            onClick={run}
            data-testid="payment-sync-run"
          >
            {sync.isPending
              ? t('orders.paymentSync.running', { defaultValue: '조회 중…' })
              : t('orders.paymentSync.run', { defaultValue: '조회 후 반영' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
